import { useSession } from "@src/appV2/Auth/api/useSession";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { Platform } from "@src/mobile/utils/platform";
import {
  addPushRegistrationErrorListener,
  addPushRegistrationListener,
  type PushNotificationListenerHandle,
  registerPushNotifications,
  requestPushPermissions,
} from "@src/mobile/utils/pushNotifications.adapter";
import { useCallback, useEffect, useRef } from "react";
import { z } from "zod";

import { useUpdatePushToken } from "../api/useUpdatePushToken";

const PUSH_TOKEN_STORAGE_KEY_PREFIX = "pushNotificationToken_";
const TOKEN_REREGISTRATION_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours

function getPushTokenStorageKey(userId: string): string {
  return `${PUSH_TOKEN_STORAGE_KEY_PREFIX}${userId}`;
}

interface StoredPushToken {
  token: string;
  timestamp: number;
}

const storedPushTokenSchema = z.object({
  token: z.string(),
  timestamp: z.number(),
});

function getStoredToken(userId: string): StoredPushToken | undefined {
  try {
    const stored = localStorage.getItem(getPushTokenStorageKey(userId));
    if (!stored) {
      return undefined;
    }

    return storedPushTokenSchema.parse(JSON.parse(stored));
  } catch {
    return undefined;
  }
}

function storeToken(userId: string, token: string): void {
  const entry: StoredPushToken = { token, timestamp: Date.now() };
  localStorage.setItem(getPushTokenStorageKey(userId), JSON.stringify(entry));
}

function shouldSendToken(userId: string, token: string): boolean {
  const stored = getStoredToken(userId);
  if (!stored) {
    return true;
  }

  const tokenChanged = stored.token !== token; // eslint-disable-line security/detect-possible-timing-attacks
  if (tokenChanged) {
    return true;
  }

  return Date.now() - stored.timestamp > TOKEN_REREGISTRATION_INTERVAL_MS;
}

export function usePushNotificationRegistration(): void {
  const { userId, admin: isAdmin } = useSession();
  const { mutate: updatePushToken } = useUpdatePushToken({ userId });
  const listenersRef = useRef<PushNotificationListenerHandle[]>([]);

  const handleToken = useCallback(
    (token: string) => {
      if (!userId || !shouldSendToken(userId, token)) {
        return;
      }

      updatePushToken(
        { pushToken: token, platform: Platform.platform },
        {
          onSuccess: () => {
            storeToken(userId, token);
          },
        }
      );
    },
    [updatePushToken, userId]
  );

  useEffect(() => {
    // Don't register push notifications for non-mobile devices, admins, or unauthenticated users
    if (!Platform.isMobile || !userId || isAdmin) {
      return;
    }

    let isCancelled = false;

    void (async () => {
      try {
        const permissionResult = await requestPushPermissions();
        if (isCancelled || permissionResult.receive !== "granted") {
          return;
        }

        const registrationListener = await addPushRegistrationListener((pushToken) => {
          handleToken(pushToken.value);
        });

        const errorListener = await addPushRegistrationErrorListener((error) => {
          logError(APP_V2_APP_EVENTS.PUSH_NOTIFICATION_REGISTRATION_FAILURE, {
            error,
          });
        });

        if (!isCancelled) {
          listenersRef.current = [registrationListener, errorListener];
        } else {
          void registrationListener.remove();
          void errorListener.remove();
          return;
        }

        await registerPushNotifications();
      } catch (error) {
        logError(APP_V2_APP_EVENTS.PUSH_NOTIFICATION_REGISTRATION_FAILURE, {
          error,
        });
      }
    })();

    return () => {
      isCancelled = true;
      for (const listener of listenersRef.current) {
        void listener.remove();
      }
      listenersRef.current = [];
    };
  }, [handleToken, isAdmin, userId]);
}
